import AppliedReferral from '@atoms/AppliedReferral';
import BPTnc from '@atoms/BPTnc';
import BPTncCheckBox from '@atoms/BPTncCheckBox';
import { Button, Divider, Text, View } from '@components/primitives';
import BPRequestAccountSelector from '@molecules/BPRequestAccountSelector';
import { logTxClosedWithSource } from '@molecules/RequestTransactionSheet/analyticsRequestTxSheet';
import appStyles from '@root/appStyles';
import colors from '@theme/colors';
import { scale, verticalScale } from '@utils/normalize';
import { useBottomSafeAreaPadding } from '@utils/safeAreaUtils';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Platform, ScrollView, StyleProp, ViewStyle } from 'react-native';
import { logTermsPage, logTxConfirmation } from './analyticsTransaction';
import { showProdDetailsFromConfigAndApi } from './bpTnCAndBillDetaillsUtils';
import styles from './styles';
import { useNavigation } from '@react-navigation/native';

interface ProductConfig {
  transaction_category_id: string;
  requestInfo: {
    header: Array<{
      key: string;
      value: string | object;
      valueFromApi: boolean;
      isCurrency?: boolean;
    }>;
    detail: Array<{
      key: string;
      value: string;
      valueFromApi: boolean;
      isCurrency?: boolean;
    }>;
    summary: Array<{
      key: string;
      value: string;
      valueFromApi: boolean;
      isCurrency?: boolean;
    }>;
  };
  [key: string]: any;
}

interface BPReqData {
  id: string;
  paymentMethods: Array<{
    id: string | number;
    [key: string]: any;
  }>;
  [key: string]: any;
}

interface SelectedBPAccount {
  id: number;
  amplitudeAccountName: string;
  [key: string]: any;
}

interface Props {
  promocode?: string;
  [key: string]: any;
}

interface MeAmplitudeContext {
  event: string[];
  key: string;
  value: string | number;
}

interface BPTncAndBillDetailsProps {
  isLoading?: boolean;
  source?: string;
  validatedReferralCode?: string;
  tncOpen?: boolean;
  productConfig: ProductConfig;
  props: Props;
  bpReqData: BPReqData;
  tncChecked?: boolean;
  confirmBP: (params: {
    providerId: string;
    employeeAccountNumber: number;
    description: string;
    referralCode: string | null;
    donatorCount: null;
    donatorName: null;
    transactionId: string;
  }) => void;
  setTncOpen: (open: boolean) => void;
  setTncChecked: (checked: boolean) => void;
  setOpenRTS: (open: boolean) => void;
  selectedBPAccount: SelectedBPAccount;
  setSelectedBPAccount: (account: SelectedBPAccount) => void;
  meAmplitudeContext: MeAmplitudeContext[];
}

function getCode(props: Props, validatedReferralCode: string): string | null {
  return validatedReferralCode;
}

function BPTncAndBillDetails({
  isLoading = false,
  validatedReferralCode = '',
  tncOpen = false,
  productConfig,
  props = { promocode: null },
  bpReqData = { id: '', paymentMethods: [] },
  tncChecked = false,
  confirmBP = () => {},
  setTncOpen = () => {},
  setTncChecked = () => {},
  setOpenRTS = () => {},
  selectedBPAccount = { id: 0, amplitudeAccountName: '' },
  setSelectedBPAccount = () => {},
  source = '',
  meAmplitudeContext = [],
}: BPTncAndBillDetailsProps) {
  const { t } = useTranslation('common');
  const bottomSafeAreaPadding = useBottomSafeAreaPadding();
  const navigation = useNavigation();
  if (tncOpen) {
    logTermsPage(productConfig, source, validatedReferralCode);
    return (
      <BPTnc
        setTncChecked={setTncChecked}
        setTncOpen={setTncOpen}
        transactionCategoryId={productConfig.transaction_category_id}
        source={source}
      />
    );
  }

  return (
    <View flex={1}>
      <View
        // showsVerticalScrollIndicator={false}
        style={{
          marginHorizontal: scale(16),
          // marginBottom: verticalScale(10),
          flex: 1,
          alignSelf: 'stretch',
        }}>
        {showProdDetailsFromConfigAndApi({
          configArray: productConfig.requestInfo.header,
          t,
          props,
        })}
        <Divider height="4px" color={colors.neutral.mist} style={appStyles.marginVertical(12)} />
        <Text variant="md-bold">{t('bp.bpDetails')}</Text>
        {showProdDetailsFromConfigAndApi({
          configArray: productConfig.requestInfo.detail,
          t,
          props,
        })}
        <Divider height="1px" color={colors.neutral.mist} style={appStyles.marginTop(12)} />
        {showProdDetailsFromConfigAndApi({
          configArray: productConfig.requestInfo.summary,
          t,
          props,
          isSummary: true,
        })}
        <Text variant="md-bold" style={appStyles.marginVertical(12)}>
          {t('reqTxSheet.bpDetails')}
        </Text>
        <BPRequestAccountSelector
          paymentMethods={bpReqData.paymentMethods}
          setSelectedBPAccount={setSelectedBPAccount}
          source={source}
          bpReqData={bpReqData}
          productConfig={productConfig}
        />
        <AppliedReferral />
        <BPTncCheckBox
          tncChecked={tncChecked}
          setTncOpen={setTncOpen}
          setTncChecked={setTncChecked}
          transaction_category_id={productConfig.transaction_category_id}
          source={source}
        />
      </View>
      <View
        style={[
          styles.rtsFooter as StyleProp<ViewStyle>,
          {
            paddingHorizontal: 16,
            paddingTop: verticalScale(10),
            marginTop: verticalScale(16),
            borderTopWidth: 1,
            borderTopColor: '#E5E7EB',
          },
        ]}>
        <Button
          testID="btnCloseRTS"
          variant="ghost"
          flex={1}
          paddingY={scale(8)}
          // minHeight={52}
          style={styles.footerButton}
          onPress={() => {
            logTxClosedWithSource(productConfig, source, 'Batal');
            navigation.goBack();
          }}>
          {t('common.cancelled')}
        </Button>
        <Button
          testID="btnOpenRTS"
          variant="solid"
          flex={1}
          paddingY={scale(8)}
          // minHeight={52}
          style={styles.footerButton}
          isDisabled={!tncChecked}
          isLoading={isLoading}
          onPress={() => {
            if (!isLoading) {
              logTxConfirmation(
                productConfig,
                bpReqData,
                source,
                validatedReferralCode,
                selectedBPAccount,
                meAmplitudeContext,
              );
              confirmBP({
                providerId: bpReqData.id,
                employeeAccountNumber: selectedBPAccount.id,
                description: '',
                referralCode: getCode(props, validatedReferralCode),
                donatorCount: null,
                donatorName: null,
                transactionId: bpReqData.id,
              });
            }
          }}>
          {t('common.confirmed')}
        </Button>
      </View>
      {/* <View flex={1} backgroundColor="red">
        <Text>View 1</Text>
      </View>
      <View backgroundColor="blue">
        <Text>View 1</Text>
      </View> */}
    </View>
  );
}

export default BPTncAndBillDetails;
